import type { BLOCK_TYPE } from '../enums'
import type { ReferenceId } from '../ids'
import type { BlockReference } from '../references'

import type { IBlock, BlockBaseSettings } from './base'

export interface PersonalInformationBlockReferences extends BlockReference {
  meaning: 'address autofill' | 'contact'
}

export type CommonFieldSettings = {
  /** Whether the field should be disabled */
  display: boolean
  /** Whether the filed is required */
  required: boolean
  /** If the field has options */
  options?: string[]
  /** If the field has a default value */
  default?: string
}

export enum PersonalInformationBlockMode {
  /**
   * When the block is configured to only create a contact entity
   */
  PRIVATE = 'private',
  /**
   * When the block is configured to create a contact and an account entity
   * All fields are configurable
   */
  BUSINESS = 'business',
  /**
   * When the block is configured to allow users to switch between the private and business mode
   * All fields are configurable
   */
  USER_DEFINED = 'user-defined'
}

export interface PrivatePIBlockFields {
  salutation: CommonFieldSettings
  title: CommonFieldSettings
  firstName: CommonFieldSettings
  lastName: CommonFieldSettings
  email: CommonFieldSettings
  telephone: CommonFieldSettings
  birthDate: CommonFieldSettings
}

export interface BusinessPIBlockFields {
  companyName: CommonFieldSettings
  registryCourt: CommonFieldSettings
  registerNumber: CommonFieldSettings
}

/**
 * This block is used to represent a private personal information block
 * This will create a contact entity with the private fields
 */
export interface PrivatePIBlock extends IBlock {
  type: BLOCK_TYPE.PERSONAL_INFORMATION
  settings: BlockBaseSettings & {
    mode: PersonalInformationBlockMode.PRIVATE
    fields: PrivatePIBlockFields
    purpose: string[]
  }
  references: Record<ReferenceId, PersonalInformationBlockReferences>
}

/**
 * This block allows to create a business account entity
 * It will also be used to create a contact entity with the private fields
 */
export interface BusinessPIBlock extends IBlock {
  type: BLOCK_TYPE.PERSONAL_INFORMATION
  settings: BlockBaseSettings & {
    mode: PersonalInformationBlockMode.BUSINESS
    /**
     * Even when the mode is business, we need to include the private fields
     * because the private fields are used to a contact entity
     */
    fields: PrivatePIBlock & BusinessPIBlockFields
    purpose: string[]
  }
  references: Record<ReferenceId, PersonalInformationBlockReferences>
}

/**
 * This block allows users to toggle between the private and business mode
 * This block will create contact (when the mode is private) or a contact and an account entity (when the mode is business)
 */
export interface UserDefinedPIBlock extends IBlock {
  type: BLOCK_TYPE.PERSONAL_INFORMATION
  settings: BlockBaseSettings & {
    mode: PersonalInformationBlockMode.USER_DEFINED
    fields: PrivatePIBlock & BusinessPIBlockFields
    purpose: string[]
  }
  references: Record<ReferenceId, PersonalInformationBlockReferences>
}

export type PersonalInformationBlock =
  | PrivatePIBlock
  | BusinessPIBlock
  | UserDefinedPIBlock
